import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { uuid } from '@/lib/utils';

export interface ProductFeedback {
  id: string;
  type: 'bug' | 'feature' | 'improvement';
  module: string;
  title: string;
  description: string;
  screenshot_path: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  approval_status: 'pending_approval' | 'approved' | 'rejected' | null;
  reported_by_user_id: string;
  assigned_to_user_id: string | null;
  admin_notes: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  reported_by?: {
    id: string;
    first_name: string;
    last_name: string;
    display_name: string;
    email: string;
  };
  assigned_to?: {
    id: string;
    first_name: string;
    last_name: string;
    display_name: string;
    email: string;
  };
}

interface CreateFeedbackData {
  type: 'bug' | 'feature' | 'improvement';
  module: string;
  title: string;
  description: string;
  screenshot?: File;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

interface UpdateFeedbackData {
  id: string;
  type?: 'bug' | 'feature' | 'improvement';
  status?: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  approval_status?: 'pending_approval' | 'approved' | 'rejected' | null;
  assigned_to_user_id?: string | null;
  admin_notes?: string | null;
}

export const useProductFeedback = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Get current app_user id
  const { data: currentAppUser } = useQuery({
    queryKey: ['current-app-user', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch all feedback
  const { data: feedbackItems, isLoading } = useQuery({
    queryKey: ['product-feedback'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bug_reports')
        .select(`
          *,
          reported_by:app_users!bug_reports_reported_by_user_id_fkey(id, first_name, last_name, display_name, email),
          assigned_to:app_users!bug_reports_assigned_to_user_id_fkey(id, first_name, last_name, display_name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ProductFeedback[];
    },
  });

  // Upload screenshot to storage
  const uploadScreenshot = async (file: File, feedbackId: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${feedbackId}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('bug-screenshots')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    return filePath;
  };

  // Create feedback
  const createFeedbackMutation = useMutation({
    mutationFn: async (data: CreateFeedbackData) => {
      if (!currentAppUser?.id) {
        throw new Error('User not authenticated');
      }

      // Create temporary ID for the feedback
      const tempId = uuid();

      // Upload screenshot only if provided
      let screenshotPath = null;
      if (data.screenshot) {
        screenshotPath = await uploadScreenshot(data.screenshot, tempId);
      }

      // Insert feedback record
      // Features/Improvements start as pending_approval, Bugs are null
      const approvalStatus = (data.type === 'feature' || data.type === 'improvement') 
        ? 'pending_approval' 
        : null;

      const { data: feedback, error } = await supabase
        .from('bug_reports')
        .insert({
          type: data.type,
          module: data.module,
          title: data.title,
          description: data.description,
          screenshot_path: screenshotPath,
          priority: data.priority,
          reported_by_user_id: currentAppUser.id,
          status: 'open',
          approval_status: approvalStatus,
        })
        .select()
        .single();

      if (error) throw error;
      return feedback;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-feedback'] });
      toast.success('Feedback submitted successfully!');
    },
    onError: (error) => {
      console.error('Error creating feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    },
  });

  // Update feedback
  const updateFeedbackMutation = useMutation({
    mutationFn: async (data: UpdateFeedbackData) => {
      const updateData: any = { ...data };
      delete updateData.id;

      // If status is being changed to resolved, set resolved_at
      if (data.status === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
      }

      const { data: feedback, error } = await supabase
        .from('bug_reports')
        .update(updateData)
        .eq('id', data.id)
        .select()
        .single();

      if (error) throw error;
      return feedback;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-feedback'] });
      toast.success('Feedback updated successfully!');
    },
    onError: (error) => {
      console.error('Error updating feedback:', error);
      toast.error('Failed to update feedback. Please try again.');
    },
  });

  // Delete feedback
  const deleteFeedbackMutation = useMutation({
    mutationFn: async (feedbackId: string) => {
      // Get feedback to find screenshot path
      const { data: feedback } = await supabase
        .from('bug_reports')
        .select('screenshot_path')
        .eq('id', feedbackId)
        .single();

      // Delete screenshot from storage
      if (feedback?.screenshot_path) {
        await supabase.storage
          .from('bug-screenshots')
          .remove([feedback.screenshot_path]);
      }

      // Delete feedback record
      const { error } = await supabase
        .from('bug_reports')
        .delete()
        .eq('id', feedbackId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-feedback'] });
      toast.success('Feedback deleted successfully!');
    },
    onError: (error) => {
      console.error('Error deleting feedback:', error);
      toast.error('Failed to delete feedback. Please try again.');
    },
  });

  // Get screenshot URL with signed URL for private bucket
  const getScreenshotUrl = async (screenshotPath: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('bug-screenshots')
        .createSignedUrl(screenshotPath, 3600); // Expires in 1 hour
      
      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }
      
      return data.signedUrl;
    } catch (error) {
      console.error('Error getting screenshot URL:', error);
      return null;
    }
  };

  return {
    feedbackItems,
    isLoading,
    createFeedback: createFeedbackMutation.mutate,
    updateFeedback: updateFeedbackMutation.mutate,
    deleteFeedback: deleteFeedbackMutation.mutate,
    isCreating: createFeedbackMutation.isPending,
    isUpdating: updateFeedbackMutation.isPending,
    isDeleting: deleteFeedbackMutation.isPending,
    getScreenshotUrl,
  };
};
